import React from 'react';
import PropTypes from 'prop-types';
import { Drawer, Descriptions, Tag, Statistic, Row, Col, Card, Progress, Divider } from 'antd';
import {
  TeamOutlined,
  UserOutlined,
  FileOutlined,
  ThunderboltOutlined,
  CloudServerOutlined,
  DatabaseOutlined,
  HddOutlined
} from '@ant-design/icons';

const TenantDetailsDrawer = ({ open, onClose, tenant, tenantStats }) => {
  if (!tenant) return null;

  const isTraining = tenant.tier === 'training';
  const tokenUsagePercentage = tenantStats?.usage_percentage || 0;
  const tokenLimit = tenant.token_limit_millions || 20;
  const tokensUsed = tenantStats?.total_tokens_used || 0;
  const tokensUsedMillions = (tokensUsed / 1000000).toFixed(2);

  const getUsageStatus = () => {
    if (tokenUsagePercentage > 90) return 'exception';
    if (tokenUsagePercentage > 75) return 'normal';
    return 'success';
  };

  return (
    <Drawer
      title={
        <div>
          <div style={{ fontSize: 20, fontWeight: 600 }}>{tenant.name}</div>
          <div style={{ fontSize: 14, color: 'var(--rlc-text-secondary)', fontWeight: 400 }}>Tenant Details</div>
        </div>
      }
      placement="right"
      onClose={onClose}
      open={open}
      width={600}
    >
      {/* Overview Stats */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={8}>
          <Card size="small">
            <Statistic
              title="Users"
              value={tenantStats?.user_count || 0}
              prefix={<UserOutlined />}
              valueStyle={{ fontSize: 24 }}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card size="small">
            <Statistic
              title="Archive Items"
              value={tenantStats?.archive_count || 0}
              prefix={<FileOutlined />}
              valueStyle={{ fontSize: 24 }}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card size="small">
            <Statistic
              title="Token Usage"
              value={`${tokenUsagePercentage.toFixed(0)}%`}
              prefix={<ThunderboltOutlined />}
              valueStyle={{
                fontSize: 24,
                color: tokenUsagePercentage > 75 ? 'var(--rlc-state-error)' : 'var(--rlc-state-success)'
              }}
            />
          </Card>
        </Col>
      </Row>

      <Divider orientation="left">Basic Information</Divider>

      {/* Basic Info */}
      <Descriptions column={1} bordered size="small">
        <Descriptions.Item label="Tenant ID">
          <code style={{ background: 'var(--rlc-surface-sunken)', padding: '2px 6px', borderRadius: 3 }}>
            {tenant.tenant_id || tenant.id}
          </code>
        </Descriptions.Item>
        <Descriptions.Item label="Tenant Name">{tenant.name}</Descriptions.Item>
        <Descriptions.Item label="Email">{tenant.email || 'N/A'}</Descriptions.Item>
        <Descriptions.Item label="Class">
          <Tag color={isTraining ? 'blue' : 'purple'}>
            {isTraining ? 'Training' : 'Client'}
          </Tag>
        </Descriptions.Item>
        <Descriptions.Item label="Status">
          <Tag color={tenant.status === 'READY' ? 'green' : 'orange'}>
            {tenant.status || 'UNKNOWN'}
          </Tag>
        </Descriptions.Item>
        <Descriptions.Item label="Created">
          {tenant.created_at ? new Date(tenant.created_at).toLocaleString() : 'N/A'}
        </Descriptions.Item>
        <Descriptions.Item label="Created By">{tenant.created_by || 'system'}</Descriptions.Item>
      </Descriptions>

      <Divider orientation="left">Token Usage</Divider>

      {/* Token Usage Details */}
      <Card size="small" style={{ marginBottom: 24 }}>
        <div style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
            <span>Tokens Used:</span>
            <span style={{ fontWeight: 600 }}>{tokensUsedMillions}M / {tokenLimit}M</span>
          </div>
          <Progress
            percent={Math.min(tokenUsagePercentage, 100)}
            status={getUsageStatus()}
            strokeWidth={12}
          />
        </div>
        <Row gutter={16}>
          <Col span={12}>
            <div style={{ fontSize: 12, color: 'var(--rlc-text-secondary)' }}>Token Limit</div>
            <div style={{ fontSize: 18, fontWeight: 600 }}>{tokenLimit}M</div>
          </Col>
          <Col span={12}>
            <div style={{ fontSize: 12, color: 'var(--rlc-text-secondary)' }}>Tokens Used</div>
            <div style={{ fontSize: 18, fontWeight: 600 }}>{tokensUsedMillions}M</div>
          </Col>
        </Row>
      </Card>

      <Divider orientation="left">Resource Counts</Divider>

      {/* Resource Counts */}
      <Descriptions column={1} bordered size="small" style={{ marginBottom: 24 }}>
        <Descriptions.Item label="Active Users">{tenantStats?.user_count || 0}</Descriptions.Item>
        <Descriptions.Item label="Archive Documents">{tenantStats?.archive_count || 0}</Descriptions.Item>
        <Descriptions.Item label="Projects">{tenantStats?.project_count || 0}</Descriptions.Item>
        <Descriptions.Item label="Conversations">{tenantStats?.conversation_count || 0}</Descriptions.Item>
      </Descriptions>

      <Divider orientation="left">Infrastructure</Divider>

      {/* AWS Resources */}
      {isTraining ? (
        <Card size="small">
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <Tag color="green" style={{ fontSize: 14, padding: '4px 12px' }}>Local Infrastructure</Tag>
            <div style={{ marginTop: 12, color: 'var(--rlc-text-secondary)', fontSize: 13 }}>
              Shared platform resources (RDS, S3, ECS)
            </div>
          </div>
        </Card>
      ) : (
        <Descriptions column={1} bordered size="small">
          <Descriptions.Item label={<><CloudServerOutlined /> AWS Account</>}>
            {tenant.aws_account_id || 'Pending...'}
          </Descriptions.Item>
          <Descriptions.Item label={<><DatabaseOutlined /> RDS Instance</>}>
            {tenant.rds_endpoint || 'Not provisioned'}
          </Descriptions.Item>
          <Descriptions.Item label={<><HddOutlined /> S3 Bucket</>}>
            {tenant.s3_bucket_name || 'Not provisioned'}
          </Descriptions.Item>
          <Descriptions.Item label="Bedrock KB">
            {tenant.bedrock_kb_id || 'N/A'}
          </Descriptions.Item>
          <Descriptions.Item label="Resources Status">
            <Tag color={tenant.resources_setup ? 'green' : 'orange'}>
              {tenant.resources_setup ? 'Provisioned' : 'Pending'}
            </Tag>
          </Descriptions.Item>
        </Descriptions>
      )}
    </Drawer>
  );
};

TenantDetailsDrawer.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  tenant: PropTypes.shape({
    id: PropTypes.string,
    tenant_id: PropTypes.string,
    name: PropTypes.string,
    email: PropTypes.string,
    tier: PropTypes.string,
    status: PropTypes.string,
    created_at: PropTypes.string,
    created_by: PropTypes.string,
    token_limit_millions: PropTypes.number,
    aws_account_id: PropTypes.string,
    rds_endpoint: PropTypes.string,
    s3_bucket_name: PropTypes.string,
    bedrock_kb_id: PropTypes.string,
    resources_setup: PropTypes.bool,
  }),
  tenantStats: PropTypes.shape({
    user_count: PropTypes.number,
    archive_count: PropTypes.number,
    project_count: PropTypes.number,
    conversation_count: PropTypes.number,
    total_tokens_used: PropTypes.number,
    usage_percentage: PropTypes.number,
  }),
};

export default TenantDetailsDrawer;
